import axiosInstance from '@/plugins/axios'; // eslint-disable-line import/no-cycle

const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/A';
  const abs = Math.abs(value);
  if (abs >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
  if (abs >= 1000) return `${(value / 1000).toFixed(2)}K`;
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2);
};

const formatPercent = (value) => `${Math.round(Math.abs(value) * 100)}%`;

const sortByPeriodDesc = (points) => [...points].sort((a, b) => {
  const periodA = String(a.period || '');
  const periodB = String(b.period || '');
  return periodB.localeCompare(periodA, undefined, { numeric: true });
});

const computePercentChange = (current, previous) => {
  if (previous === 0 && current > 0) return 1;
  if (previous === 0 && current === 0) return 0;
  if (previous === 0) return null;
  return (current - previous) / previous;
};

const describeTrend = (percentChange) => {
  if (percentChange === null) return 'insufficient baseline data to calculate a period-over-period change';
  const pct = formatPercent(percentChange);
  if (percentChange > 0.3) return `a sharp increase of ${pct} compared to the previous period`;
  if (percentChange > 0.1) return `a moderate increase of ${pct} compared to the previous period`;
  if (percentChange > 0.02) return `a slight increase of ${pct} compared to the previous period`;
  if (percentChange < -0.3) return `a sharp decrease of ${pct} compared to the previous period`;
  if (percentChange < -0.1) return `a moderate decrease of ${pct} compared to the previous period`;
  if (percentChange < -0.02) return `a slight decrease of ${pct} compared to the previous period`;
  return `relative stability with only ${pct} variance from the previous period`;
};

const buildImplication = ({
  indicatorName, locationName, percentChange, currentValue, previousValue, currentPeriod,
}) => {
  if (percentChange === null) {
    return `With only one usable data point for ${indicatorName} in ${locationName}, trend interpretation is limited. Verify reporting completeness for ${currentPeriod} before drawing programmatic conclusions.`;
  }

  if (percentChange > 0.3) {
    return `The ${formatPercent(percentChange)} rise from ${formatNumber(previousValue)} to ${formatNumber(currentValue)} warrants immediate review. Confirm whether this reflects improved service delivery, a reporting surge, or a data-quality issue before scaling interventions.`;
  }
  if (percentChange < -0.3) {
    return `The ${formatPercent(percentChange)} drop from ${formatNumber(previousValue)} to ${formatNumber(currentValue)} is a priority signal. Cross-check facility reporting, stock availability, and regional M&E records in ${locationName} to identify operational causes.`;
  }
  if (Math.abs(percentChange) > 0.1) {
    return `The measured shift between ${formatNumber(previousValue)} and ${formatNumber(currentValue)} suggests a meaningful change that should be tracked over the next reporting cycle in ${locationName}.`;
  }
  return `Performance for ${indicatorName} in ${locationName} is broadly stable between ${formatNumber(previousValue)} and ${formatNumber(currentValue)}. Maintain routine monitoring unless external shocks affect the program area.`;
};

const buildBriefFromDataPoints = (dataPoints, {
  indicatorName, locationName, datasourceName,
}) => {
  const current = dataPoints[0];
  const previous = dataPoints.length > 1 ? dataPoints[1] : null;
  const percentChange = previous ? computePercentChange(current.value, previous.value) : null;

  const values = dataPoints.map((point) => point.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const avgValue = values.reduce((sum, val) => sum + val, 0) / values.length;
  const periodsCovered = [...new Set(dataPoints.map((point) => point.period))];

  const trendSummary = previous
    ? `For ${indicatorName} in ${locationName} (${current.period}), the latest recorded value is ${formatNumber(current.value)} from ${datasourceName}, showing ${describeTrend(percentChange)}. The prior period (${previous.period}) recorded ${formatNumber(previous.value)}.`
    : `For ${indicatorName} in ${locationName}, the available record for period ${current.period} shows a value of ${formatNumber(current.value)} from ${datasourceName}. Only one period is available, so period-over-period comparison is not possible.`;

  const observations = [
    `Latest value: ${formatNumber(current.value)} in period ${current.period} for ${current.location}.`,
  ];

  if (previous) {
    const direction = percentChange >= 0 ? 'increase' : 'decrease';
    observations.push(
      `Period-over-period change: ${formatNumber(previous.value)} (${previous.period}) to ${formatNumber(current.value)} (${current.period}), a ${formatPercent(percentChange)} ${direction}.`,
    );
  }

  observations.push(
    `Historical range across ${periodsCovered.length} period(s): minimum ${formatNumber(minValue)}, maximum ${formatNumber(maxValue)}, average ${formatNumber(avgValue)}.`,
  );

  if (dataPoints.length >= 3) {
    const deviation = ((current.value - avgValue) / avgValue) * 100;
    if (Math.abs(deviation) > 5) {
      const relation = deviation > 0 ? 'above' : 'below';
      observations.push(
        `The current value is ${formatPercent(deviation / 100)} ${relation} the historical average (${formatNumber(avgValue)}) across the retrieved records.`,
      );
    } else {
      observations.push(
        `The current value (${formatNumber(current.value)}) is close to the historical average (${formatNumber(avgValue)}) across the retrieved records.`,
      );
    }
  }

  if (current.updatedAt) {
    observations.push(`Most recent record was last updated on ${new Date(current.updatedAt).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' })}.`);
  }

  const implication = buildImplication({
    indicatorName,
    locationName,
    percentChange,
    currentValue: current.value,
    previousValue: previous ? previous.value : null,
    currentPeriod: current.period,
  });

  return {
    dataAvailable: true,
    error: false,
    trendSummary,
    observations: observations.slice(0, 4),
    implication,
    metadata: {
      currentValue: current.value,
      previousValue: previous ? previous.value : null,
      percentChange,
      currentPeriod: current.period,
      previousPeriod: previous ? previous.period : null,
      dataPointCount: dataPoints.length,
      periodsCovered,
    },
  };
};

const generateInsightBrief = async (filters) => {
  const safeFilters = filters || {};
  const {
    indicatorId,
    locationId,
    datasourceId,
    period,
    indicator: indicatorName = 'Selected indicator',
    location: locationName = 'Nigeria',
    datasource: datasourceName = 'Selected data source',
  } = safeFilters;

  if (!indicatorId) {
    return {
      dataAvailable: false,
      error: true,
      message: 'Please select an indicator before generating an insight brief.',
      trendSummary: '',
      observations: [],
      implication: '',
    };
  }

  const params = new URLSearchParams({
    indicator: indicatorId,
    ordering: '-period',
    size: '24',
  });

  if (locationId) params.append('location', locationId);
  if (datasourceId) params.append('datasource', datasourceId);
  if (period && period !== 'N/A') params.append('period', period);

  const { data } = await axiosInstance.get(`data/?${params.toString()}`);
  const results = data.results || [];

  const dataPoints = sortByPeriodDesc(
    results
      .map((row) => ({
        value: parseFloat(row.value),
        period: row.period,
        location: row.location_name || locationName,
        datasource: row.datasource_name || datasourceName,
        updatedAt: row.updated_at,
      }))
      .filter((row) => !Number.isNaN(row.value)),
  );

  if (dataPoints.length === 0) {
    const periodSuffix = period && period !== 'N/A' ? ` for period ${period}` : '';
    return {
      dataAvailable: false,
      error: true,
      message: `No data records were returned for ${indicatorName} in ${locationName} using ${datasourceName}${periodSuffix}.`,
      trendSummary: '',
      observations: [],
      implication: '',
    };
  }

  return buildBriefFromDataPoints(dataPoints, {
    indicatorName,
    locationName,
    datasourceName,
  });
};

export default generateInsightBrief;
